import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {useSettings} from '@ui/settings/use-settings';
import {User} from '@ui/types/user';
import dot from 'dot-object';
import {useMemo} from 'react';
import {useAuth} from '../auth/use-auth';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    const menuItems: MenuItemConfig[] = [];
    // Customization (change AV): when the help center knowledge base is disabled,
    // drop its links (/hc and its article/category/search pages) from every
    // navigation menu. The customer ticket portal (/hc/tickets*) is kept.
    const hcDisabled = !!settings.hc?.disabled;

    if (menu) {
      for (const item of menu.items) {
        if (hcDisabled && isHelpCenterKbLink(item.action)) {
          continue;
        }

        const hasRoles = (item.roles || []).every(a =>
          user?.roles?.find(b => b.id === a),
        );
        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );
        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        if (
          !item.action ||
          !hasRoles ||
          !hasPermissions ||
          !hasSettings ||
          !subscriptionStatusMatches(item, user)
        ) {
          continue;
        }

        if (user?.id) {
          item.action = item.action.replace(/{currentUser}/g, `${user.id}`);
        }

        menuItems.push(item);
      }
      menu.items = menuItems;
    }

    return menu;
  }, [hasPermission, settings, menuOrPosition, user]);
}

// Customization (change AV): a help center knowledge-base link — "/hc" and its
// article/category/search pages — but NOT the customer ticket portal
// ("/hc/tickets", "/hc/tickets/..."), which stays available when the help center
// is disabled. See _changes_3.0.8.php.
function isHelpCenterKbLink(action?: string): boolean {
  if (!action) return false;
  const path = action.split('?')[0];
  return /^\/hc(\/(?!tickets(\/|$)).*)?$/.test(path);
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
